import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { supabase } from '../../config/supabase'; // Importação do Supabase
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Trash2, PlusCircle, Edit2, X } from 'lucide-react';
import { prepareImageForUpload, uploadNewsImage } from '../../utils/storage';
import { NewsItem, NewsForm } from '../../types/supabase'; // Novas interfaces/types

const NewsEditor = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { user, selectedClient } = useAuth();
  const currentClientId = selectedClient?.clientId;
  const navigate = useNavigate();
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<NewsForm>();

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !user.userId) { // Assumindo que user.userId é o userId do Supabase
        navigate('/login');
        return;
      }

      if (!currentClientId) {
        toast.error('Selecione uma clínica para gerenciar notícias.');
        navigate('/selecionar-cliente');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_clients')
          .select('userRole')
          .eq('userId', user.userId)
          .eq('clientId', currentClientId)
          .single();

        if (error) {
          throw new Error(error.message || 'Erro ao buscar perfil do usuário');
        }

        const isAdmin = data?.userRole === 'admin';

        if (!isAdmin) {
          toast.error('Acesso não autorizado');
          navigate('/');
          return;
        }

        setIsAdmin(true);
        fetchNews(currentClientId);
      } catch (error: any) {
        console.error('Erro ao verificar permissões:', error.message);
        toast.error('Erro ao verificar permissões');
        navigate('/');
      }
    };

    checkAdminStatus();
  }, [user, currentClientId, navigate]);

  const fetchNews = async (clientId?: string) => {
    const scopedClientId = clientId ?? currentClientId;
    if (!scopedClientId) {
      setNewsList([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('newspage')
        .select('*')
        .eq('clientId', scopedClientId)
        .order('newspagePublishDate', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      // Mapear os dados do Supabase para a interface NewsItem esperada
      const news = data.map(item => ({
        newspageId: item.newspageId,
        userId: item.userId,
        clientId: item.clientId,
        newspageTitle: item.newspageTitle,
        newspageExcerpt: item.newspageExcerpt,
        newspageContent: item.newspageContent,
        newspageImage: item.newspageImage,
        newspageCategory: item.newspageCategory,
        newspagePublishDate: item.newspagePublishDate, // Ajustar conforme o tipo de retorno do Supabase
        newspageAuthor: item.newspageAuthor,
      })) as NewsItem[];

      setNewsList(news);
    } catch (error: any) {
      console.error('Erro ao carregar notícias:', error.message);
      toast.error('Erro ao carregar notícias');
    }
  };

  const handleEdit = (news: NewsItem) => {
    setEditingNews(news);
    setShowEditor(true);
    setSelectedFile(null);
    setValue('title', news.newspageTitle);
    setValue('excerpt', news.newspageExcerpt);
    setValue('content', news.newspageContent);
    setValue('category', news.newspageCategory);
    // Campos 'enableLeadCapture', 'leadButtonText', 'leadType' removidos conforme decisão prévia
    // Se forem adicionados ao schema, deverão ser mapeados aqui
    setImagePreview(news.newspageImage);
  };

  const handleImageChange = async (file: File) => {
    setIsPreparingImage(true);

    try {
      const preparedFile = await prepareImageForUpload(file);
      const previewUrl = URL.createObjectURL(preparedFile);

      if (preparedFile.size < file.size) {
        toast.info('Imagem otimizada automaticamente para o upload.');
      }

      setSelectedFile(preparedFile);
      setImagePreview(previewUrl);
    } catch (error) {
      console.error('Error handling image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar imagem';
      toast.error(errorMessage);
    } finally {
      setIsPreparingImage(false);
    }
  };

  const onSubmit = async (data: NewsForm) => {
    if (!user || !isAdmin || !currentClientId) {
      toast.error('Você não tem permissão para publicar notícias');
      return;
    }

    if (!selectedFile && !imagePreview) {
      toast.error('Adicione uma imagem para a notícia');
      return;
    }

    setIsLoading(true);
    try {
      let imageUrl = imagePreview;
      
      // Only upload new image if a file is selected
      if (selectedFile) {
        imageUrl = await uploadNewsImage(selectedFile);
      }

      if (!imageUrl) {
        throw new Error('Erro ao processar imagem');
      }

      const now = new Date().toISOString(); // Supabase usa ISO string para timestamps

      const newsData = {
        userId: user.userId, // ID do usuário do Supabase
        clientId: currentClientId,
        newspageTitle: data.title.trim(),
        newspageExcerpt: data.excerpt.trim(),
        newspageContent: data.content.trim(),
        newspageImage: imageUrl,
        newspageCategory: data.category,
        newspageAuthor: user.userDisplayName || 'Admin',
        newspagePublishDate: now,
        // Campos 'enableLeadCapture', 'leadButtonText', 'leadType' removidos
        // 'status' não está no schema do Supabase, `newspageFormattedContent` não está sendo usado
        // createdAt e updatedAt são default CURRENT_TIMESTAMP no schema, se não forem passados
      };

      if (editingNews) {
        const { error } = await supabase
          .from('newspage')
          .update(newsData)
          .eq('newspageId', editingNews.newspageId)
          .eq('clientId', currentClientId);

        if (error) throw new Error(error.message);
        toast.success('Notícia atualizada com sucesso!');
      } else {
        const { error } = await supabase
          .from('newspage')
          .insert(newsData);

        if (error) throw new Error(error.message);
        toast.success('Notícia publicada com sucesso!');
      }

      reset();
      setShowEditor(false);
      setEditingNews(null);
      setImagePreview(null);
      setSelectedFile(null);
      fetchNews(currentClientId);
    } catch (error) {
      console.error('Error saving news:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao salvar notícia';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (newspageId: string) => {
    if (!currentClientId) {
      toast.error('Selecione uma clínica para excluir notícias.');
      return;
    }

    try {
      const { error } = await supabase
        .from('newspage')
        .delete()
        .eq('newspageId', newspageId)
        .eq('clientId', currentClientId);

      if (error) throw new Error(error.message);

      toast.success('Notícia excluída com sucesso!');
      fetchNews(currentClientId);
    } catch (error: any) {
      console.error('Erro ao excluir notícia:', error.message);
      toast.error('Erro ao excluir notícia');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Gerenciar Notícias</h2>
        {!showEditor && (
          <button
            onClick={() => {
              setEditingNews(null);
              reset();
              setImagePreview(null);
              setSelectedFile(null);
              setShowEditor(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Nova Notícia
          </button>
        )}
      </div>

      {showEditor ? (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-semibold mb-6">
            {editingNews ? 'Editar Notícia' : 'Nova Notícia'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Título</label>
              <input
                type="text"
                {...register('title', { required: 'Título é obrigatório' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Resumo</label>
              <textarea
                {...register('excerpt', { required: 'Resumo é obrigatório' })}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              {errors.excerpt && (
                <p className="mt-1 text-sm text-red-600">{errors.excerpt.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Conteúdo</label>
              <textarea
                {...register('content', { required: 'Conteúdo é obrigatório' })}
                rows={10}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Imagem</label>
              <div className="space-y-4">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={isPreparingImage || isLoading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImageChange(file);
                    }
                  }}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-primary-50 file:text-primary-700
                    hover:file:bg-primary-100"
                />
                {isPreparingImage && (
                  <p className="text-sm text-gray-500">Processando imagem...</p>
                )}
                {imagePreview && (
                  <div className="relative w-full max-w-2xl mx-auto">
                    <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setSelectedFile(null);
                        }}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Os campos de captura de leads foram removidos conforme decisão prévia. */}

            <div>
              <label className="block text-sm font-medium text-gray-700">Categoria</label>
              <select
                {...register('category', { required: 'Categoria é obrigatória' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Selecione uma categoria</option>
                <option value="fisioterapia">Fisioterapia</option>
                <option value="reabilitacao">Reabilitação</option>
                <option value="saude">Saúde</option>
                <option value="bem-estar">Bem-estar</option>
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditor(false);
                  setEditingNews(null);
                  setImagePreview(null);
                  setSelectedFile(null);
                  reset();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading || isPreparingImage}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? 'Salvando...' : isPreparingImage ? 'Processando imagem...' : editingNews ? 'Salvar Alterações' : 'Publicar Notícia'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Título
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Data de Publicação
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Categoria
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {newsList.map((news) => (
                  <tr key={news.newspageId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {news.newspageImage && (
                          <img
                            src={news.newspageImage}
                            alt={news.newspageTitle}
                            className="h-10 w-10 rounded-md object-cover mr-3"
                          />
                        )}
                        <div className="text-sm font-medium text-gray-900">{news.newspageTitle}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {new Date(news.newspagePublishDate).toLocaleDateString("pt-BR")}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{news.newspageCategory}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(news)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Editar"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(news.newspageId)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {newsList.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">Nenhuma notícia publicada</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir esta notícia? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm!)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsEditor;
